import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format, parseISO, isValid } from "date-fns";
import { useReport } from "@/hooks/use-report";
import { getReportStatusMeta, formatOptionLabel } from "../../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    maxWidth: 520,
    width: "100%",
  },
}));

const Field = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value ?? "N/A"}
    </Typography>
  </Box>
);

const formatDateTime = (date, pattern = "MMM d, yyyy, h:mm a") => {
  if (!date) return "N/A";
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, pattern) : "N/A";
};

const ReportDetailDrawer = ({ open, onClose, reportId }) => {
  const { data, isLoading } = useReport(open ? reportId : null);
  const report = data?.data;
  const statusMeta = getReportStatusMeta(report?.status);

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Report Details
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
        {isLoading ? (
          <Skeleton variant="rounded" height={200} />
        ) : (
          <>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: "1rem" }}>
                {report?.name}
              </Typography>
              {report?.description && (
                <Typography variant="body2" color="text.secondary">
                  {report.description}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "inline-flex",
                alignSelf: "flex-start",
                px: 1.5,
                py: 0.3,
                borderRadius: "999px",
                fontSize: "0.8rem",
                fontWeight: 500,
                backgroundColor: statusMeta.bg,
                color: statusMeta.color,
              }}
            >
              {statusMeta.label}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Field label="Type" value={formatOptionLabel(report?.type)} />
              <Field label="Format" value={report?.format} />
              <Field
                label="Date Range"
                value={`${report?.dateRangeStart ?? "N/A"} - ${report?.dateRangeEnd ?? "N/A"}`}
              />
              <Field label="Generated On" value={formatDateTime(report?.generatedOn)} />
              <Field label="Created By" value={report?.createdByName} />
              <Field label="Approved By" value={report?.approvedByName} />
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Activity
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {report?.activity?.length ? (
                  report.activity.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        borderLeft: "2px solid",
                        borderColor: "divider",
                        pl: 1.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {formatOptionLabel(entry.action)}{" "}
                        <Typography component="span" variant="caption" color="text.secondary">
                          by {entry.actorName ?? "N/A"}
                        </Typography>
                      </Typography>
                      {entry.note && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {entry.note}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(entry.occurredAt)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No activity recorded yet.
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default ReportDetailDrawer;
